'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Building2, ArrowLeft, ArrowRight, Copy, Check, Mail } from 'lucide-react';

const EMAIL = 'demo@plenari.cat';

const MAILTO_HREF =
  'mailto:demo@plenari.cat?subject=Sol%C2%B7licitud%20de%20demo%20PlenariCAT&body=Hola%2C%0A%0AVoldria%20demanar%20una%20demo%20de%2030%20minuts%20de%20PlenariCAT.%0A%0ANom%3A%0AOrganitzaci%C3%B3%3A%0A%C3%80mbit%20territorial%20%2F%20municipis%3A%0ATel%C3%A8fon%20(opcional)%3A%0A%0AGr%C3%A0cies.';

export default function DemoPageClient() {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard?.writeText(EMAIL)?.then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }

  return (
    <>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-200" role="banner">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2.5 flex-shrink-0">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'var(--primary)' }}
                aria-hidden="true"
              >
                <Building2 size={16} className="text-white" />
              </div>
              <div>
                <span className="text-sm font-bold text-slate-900 leading-tight block">PlenariCAT</span>
                <span className="text-xs text-slate-500 leading-tight hidden sm:block">Treball municipal</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Link
                href="/"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors min-h-[44px]"
              >
                <ArrowLeft size={14} aria-hidden="true" />
                Torna a PlenariCAT
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main id="main-content" tabIndex={-1}>
        <section
          className="pt-16 pb-20 sm:pt-20 sm:pb-24 lg:pt-24 lg:pb-28 px-4 sm:px-6 lg:px-8 bg-slate-50"
          aria-labelledby="demo-heading"
        >
          <div className="max-w-2xl mx-auto">
            {/* Eyebrow */}
            <p className="text-xs font-semibold tracking-widest uppercase text-slate-500 mb-6 leading-none">
              Demo de PlenariCAT
            </p>

            {/* H1 */}
            <h1
              id="demo-heading"
              className="text-[2.5rem] sm:text-[3rem] lg:text-[3.25rem] font-semibold text-slate-900 leading-[1.07] tracking-tight mb-5 text-balance"
              style={{ fontFamily: 'var(--font-source-serif), Georgia, serif' }}
            >
              Descobreix PlenariCAT en 30 minuts.
            </h1>

            <p className="text-[1.0625rem] text-slate-600 leading-relaxed mb-12">
              Et mostrarem com PlenariCAT estructura la documentació d&apos;un ple, connecta antecedents i facilita la preparació i el seguiment del treball municipal.
            </p>

            {/* Contact block */}
            <div className="border border-slate-200 rounded-lg bg-white p-7 sm:p-8 mb-8">
              <h2 className="text-[1.0625rem] font-bold text-slate-900 mb-2">
                Demana una demo
              </h2>
              <p className="text-[0.9375rem] text-slate-600 leading-relaxed mb-6">
                Escriu-nos a{' '}
                <a
                  href={`mailto:${EMAIL}`}
                  className="font-medium text-slate-900 underline underline-offset-2 hover:text-slate-700 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                  style={{ outlineColor: 'var(--primary)' }}
                >
                  {EMAIL}
                </a>{' '}
                i indica, si vols, l&apos;organització i els municipis amb què treballeu.
              </p>

              {/* Email address — selectable, prominent */}
              <div
                className="flex items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg mb-6 select-all"
                aria-label={`Adreça de correu: ${EMAIL}`}
              >
                <Mail size={16} className="text-slate-400 flex-shrink-0" aria-hidden="true" />
                <span className="text-[0.9375rem] font-mono font-medium text-slate-900 flex-1 min-w-0 break-all">
                  {EMAIL}
                </span>
              </div>

              {/* Action buttons */}
              <div className="flex flex-col xs:flex-row gap-3">
                {/* Primary — mailto */}
                <a
                  href={MAILTO_HREF}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-base font-semibold text-white rounded-lg transition-colors min-h-[48px]"
                  style={{ backgroundColor: 'var(--primary)' }}
                >
                  <Mail size={18} aria-hidden="true" />
                  Escriu a {EMAIL}
                </a>

                {/* Secondary — copy */}
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-base font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors min-h-[48px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                  style={{ outlineColor: 'var(--primary)' }}
                  aria-live="polite"
                  aria-label={copied ? 'Correu copiat al porta-retalls' : 'Copia el correu al porta-retalls'}
                >
                  {copied ? (
                    <>
                      <Check size={18} className="text-emerald-600" aria-hidden="true" />
                      <span className="text-emerald-700 font-semibold">Correu copiat</span>
                    </>
                  ) : (
                    <>
                      <Copy size={18} aria-hidden="true" />
                      Copia el correu
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Secondary navigation */}
            <div className="flex flex-col xs:flex-row gap-3">
              <Link
                href="/"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors min-h-[44px]"
              >
                <ArrowLeft size={16} aria-hidden="true" />
                Torna a PlenariCAT
              </Link>
              <Link
                href="/login"
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors min-h-[44px]"
              >
                Explora la demostració
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      {/* Minimal footer */}
      <footer className="border-t border-slate-200 bg-white py-8 px-4 sm:px-6 lg:px-8" role="contentinfo">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div
              className="w-6 h-6 rounded flex items-center justify-center"
              style={{ backgroundColor: 'var(--primary)' }}
              aria-hidden="true"
            >
              <Building2 size={12} className="text-white" />
            </div>
            <span className="text-sm font-bold text-slate-900">PlenariCAT</span>
            <span className="text-xs text-slate-400 ml-1">· Plataforma de treball municipal</span>
          </div>
          <p className="text-xs text-slate-400">
            <a href={`mailto:${EMAIL}`} className="hover:text-slate-600 transition-colors">
              {EMAIL}
            </a>
          </p>
        </div>
      </footer>
    </>
  );
}
